<script setup lang="ts">
/**
 * Хаб проектов — макет 17:765: тулбар с фильтром и поиском, заголовок со
 * сводкой и сетка карточек 354×148 в три колонки.
 *
 * Фильтров три, как в макете: «Все», «Мои» и «Архив». Архивный проект уходит
 * из хаба и из сайдбара, но его задачи остаются доступны по ссылке — это не
 * удаление (DEV-1500).
 *
 * Подсказки «⌘K» у поиска тоже нет: сочетание отдано палитре быстрого перехода
 * (DEV-1482), а два разных действия на одно сочетание не уживаются.
 */
import { AppButton, AppSegmentedControl, AppTextField } from '@vinprop/design-system'
import { AlertTriangle, Folder, Search } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import OnboardingCard from '@/features/onboarding/OnboardingCard.vue'
import { listActivity, type ActivityEvent } from '@/features/projects/activity'
import ActivityFeed from '@/features/projects/components/ActivityFeed.vue'
import {
  buildSteps,
  dismiss,
  doneCount,
  isDismissed,
  loadProgress,
  type Step,
} from '@/features/onboarding/api'
import CreateProjectModal from '@/features/projects/components/CreateProjectModal.vue'
import ProjectCard from '@/features/projects/components/ProjectCard.vue'
import { useProjectsStore, type ProjectFilter } from '@/features/projects/store'

const { t } = useI18n()

const projects = useProjectsStore()
const route = useRoute()
const router = useRouter()

const filter = ref<ProjectFilter>('all')
const search = ref('')
// Плюс в сайдбаре ведёт сюда с ?create=1: заводить проект зовут с любого
// экрана, а форма живёт на хабе.
const isCreateOpen = ref(route.query.create === '1')

const filters = computed(() => [
  { value: 'all' as const, label: t('projects.filters.all') },
  { value: 'mine' as const, label: t('projects.filters.mine') },
  { value: 'archived' as const, label: t('projects.filters.archived') },
])

// Три числительных, а не одна строка: у русского три формы, и «1 проектов»
// на первом же экране нового пользователя выглядит поломкой.
const summary = computed(() =>
  [
    t('projects.summary.projects', projects.items.length),
    t('projects.summary.issues', projects.openIssueTotal),
    t('projects.summary.mine', projects.myProjectCount),
  ].join(' · '),
)

const isEmpty = computed(
  () => !projects.isLoading && !projects.error && projects.items.length === 0,
)

/** Пусто из-за фильтра — это не «нет проектов», и звать заводить первый незачем. */
const isFiltered = computed(() => filter.value !== 'all' || search.value.trim().length > 0)

const reload = () => projects.load({ filter: filter.value, search: search.value.trim() })

let searchTimer: ReturnType<typeof setTimeout> | undefined

// Запрос на каждую букву не нужен: список короткий, а бэкенд ищет по всем
// проектам, до которых человек допущен.
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(reload, 300)
})

watch(filter, reload)

/**
 * Чек-лист начала работы. Показываем, пока он не пройден целиком: подсказка
 * нужна первые дни, а потом занимает место на самом посещаемом экране.
 */
const events = ref<ActivityEvent[]>([])
const steps = ref<Step[]>([])
const isOnboardingVisible = computed(
  () => steps.value.length > 0 && doneCount(steps.value) < steps.value.length,
)

const refreshOnboarding = async () => {
  if (isDismissed()) return

  try {
    const next = buildSteps(await loadProgress(projects.items[0]?.id ?? ''))

    steps.value = next

    // Пройденный больше не считаем: два запроса на каждый заход ради строки,
    // которой уже не будет.
    if (doneCount(next) === next.length) dismiss()
  } catch {
    // Подсказка не стоит того, чтобы из-за неё экран показывал ошибку.
    steps.value = []
  }
}

// Список мог приехать раньше — его грузит лэйаут ради сайдбара; второй раз
// за тем же ходить незачем.
onMounted(async () => {
  await projects.ensureLoaded()
  await refreshOnboarding()

  try {
    events.value = await listActivity()
  } catch {
    // Лента — второстепенный блок: без неё хаб остаётся рабочим.
    events.value = []
  }
})

// Флаг в адресе одноразовый: перезагрузка страницы не должна снова открывать
// форму, а «назад» — возвращать в неё.
watch(isCreateOpen, (open) => {
  if (!open && route.query.create) void router.replace({ query: {} })
})

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
})
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.projects') }]">
    <AppSegmentedControl
      v-model="filter"
      :options="filters"
      size="small"
      :aria-label="t('projects.filters.label')"
    />

    <div class="projects__search">
      <AppTextField
        v-model="search"
        size="small"
        filled
        :placeholder="t('projects.searchPlaceholder')"
        :prefix-icon="Search"
        :aria-label="t('projects.searchPlaceholder')"
      />
    </div>

    <AppButton variant="primary" size="small" @click="isCreateOpen = true">
      {{ t('projects.create.action') }}
    </AppButton>
  </TrackerToolbar>

  <div class="projects">
    <h1 class="projects__title">{{ t('nav.projects') }}</h1>
    <p class="projects__summary">{{ summary }}</p>

    <div v-if="projects.isLoading && !projects.items.length" class="projects__grid">
      <div v-for="index in 3" :key="index" class="projects__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="projects.error"
      :icon="AlertTriangle"
      :title="t('projects.error.title')"
      :description="t('projects.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="reload">{{ t('projects.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="isEmpty && isFiltered"
      :icon="Search"
      :title="t('projects.nothingFound.title')"
      :description="t('projects.nothingFound.description')"
    />

    <TrackerEmptyState
      v-else-if="isEmpty"
      :icon="Folder"
      :title="t('projects.empty.title')"
      :description="t('projects.empty.description')"
    >
      <AppButton variant="primary" @click="isCreateOpen = true">
        {{ t('projects.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <div v-else class="projects__grid">
      <ProjectCard
        v-for="project in projects.items"
        :key="project.id"
        :project="project"
        :members="projects.members[project.id]"
      />
    </div>

    <!-- Лента под сеткой: за ней приходят реже, чем за проектами. Архивную
         вкладку она не сопровождает — там смотрят на список, а не на события. -->
    <ActivityFeed v-if="filter !== 'archived'" class="projects__feed" :events="events" />

    <OnboardingCard
      v-if="isOnboardingVisible"
      class="projects__onboarding"
      :steps="steps"
      :project-key="projects.items[0]?.key ?? ''"
      @create-project="isCreateOpen = true"
    />
  </div>

  <CreateProjectModal v-model="isCreateOpen" />
</template>

<style scoped>
.projects {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 32px 48px;
}

.projects__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-semibold);
  line-height: 23px;
}

.projects__summary {
  margin: 20px 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body);
  line-height: 16px;
}

/* Три колонки по 354 при ширине контента 1104 — как в макете; на узком экране
   ряд разбирается сам. */
.projects__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px 21px;
  margin-top: 20px;
}

.projects__skeleton {
  height: 148px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.projects__search {
  width: 220px;
}

/* Чек-лист стоит под сеткой: он подсказка, а не первое, за чем сюда приходят. */
.projects__onboarding {
  margin-top: 24px;
}

.projects__feed {
  margin-top: 28px;
}

</style>
